import { FileDetailsTabs, FileResponseInterface, FileUpload } from 'services/interface';
import { useCallback } from 'react';
import { navigate } from 'gatsby';
import { axios, AxiosConfig, MutationFetcher, SWRKey, useSWRConfig, useSWRMutation } from 'services/swr';
import { useToastNotify } from '@opswat/ui';
import { useApikeyLimits } from 'services/api/ApikeyLimits';
import { useLimitPrompt } from 'services/LimitPrompt';
import { useUser } from 'services/User';
import { isPaidUser } from 'services/accessControl';
import { getFileHash } from 'utils/file/Format';

const getArchivePasswordHeaders = (passwords?: string[]): Record<string, string> => {
    if (!passwords || passwords.length === 0) {
        return {};
    }

    const filteredPasswords = passwords.map((password) => password.trim()).filter((password) => password !== '');

    if (filteredPasswords.length === 0) {
        return {};
    }

    if (filteredPasswords.length === 1) {
        return { archivepwd: filteredPasswords[0] };
    }

    return filteredPasswords.reduce<Record<string, string>>((headers, password, index) => {
        headers[`archivepwd${index + 1}`] = password;
        return headers;
    }, {});
};

const uploadFile: MutationFetcher<
    FileUpload,
    SWRKey,
    {
        file?: File;
        // When set, the backend downloads and scans the file from this URL (downloadfrom header) instead of a body.
        downloadUrl?: string;
        rules?: string;
        sandbox?: boolean;
        privateScan?: boolean;
        archivePasswords?: string[];
        sendArchivePasswords?: boolean;
        onProgress?: (progress: number) => void;
        signal?: AbortSignal;
    }
> = async (
    { url, ...config },
    {
        arg: {
            file,
            downloadUrl,
            rules,
            sandbox,
            privateScan,
            archivePasswords,
            sendArchivePasswords,
            onProgress,
            signal,
        },
    },
) => {
    let data: FormData | undefined;
    if (file) {
        data = new FormData();
        data.append('file', file);
    }

    const hasUnarchiveRule = Boolean(rules?.split(',').some((rule) => rule.trim().toLowerCase() === 'unarchive'));

    const shouldSendArchivePasswords = Boolean(
        archivePasswords?.some((password) => password.trim() !== '') && (sendArchivePasswords || hasUnarchiveRule),
    );

    const headers = {
        ...config.headers,
        rule: rules,
        ...(file && { filename: encodeURIComponent(file.name) }),
        ...(downloadUrl && { downloadfrom: downloadUrl }),
        ...(sandbox && { sandbox: 'windows10' }),
        ...(privateScan && { privateprocessing: '1' }),
        ...(shouldSendArchivePasswords ? getArchivePasswordHeaders(archivePasswords) : {}),
    };

    const res = await axios.post(url, data, {
        ...config,
        headers,
        signal,
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
            }
        },
    });

    return res.data;
};

/**
 * The File behind the most recent successful upload, keyed by the `data_id` it produced — or, for a
 * community hash-first hit, the `data_id` of the existing report it landed on.
 *
 * A results page that has to re-submit the same bytes (MDCI-985: a password-protected file the
 * backend could not open) holds only the id from the URL — the body is long gone by then. Module
 * scope rather than Gatsby navigation state: a File does survive a structured clone, but the 140MB
 * one this app accepts does not survive the history-state size cap. Lost on a hard reload, which is
 * correct — the retry prompt belongs to the upload that just happened, not to an old result someone
 * opened by link.
 *
 * ponytail: one slot, not a map — only the newest upload can be the one the user is looking at.
 */
export type ScanSubmission = {
    file: File;
    rules?: string;
    sandbox?: boolean;
    privateScan?: boolean;
};

// Deliberately holds no password: the retry sends what the user types straight to the header.
let lastScanSubmission: (ScanSubmission & { dataId: string }) | null = null;

export const getScanSubmission = (dataId?: string): ScanSubmission | undefined =>
    dataId && lastScanSubmission?.dataId === dataId ? lastScanSubmission : undefined;

const hashLookupKey: SWRKey = { url: '/v4/hash/' };

const hashLookup: MutationFetcher<FileResponseInterface, SWRKey, string> = async (
    { url, ...config },
    { arg: hash },
) => {
    const hashUrl = url + hash;
    const res = await axios.get(hashUrl, config);
    return res.data;
};

const hashLookupSandboxKey: SWRKey = { url: '/v4/hash/' };

const hashLookupSandbox: MutationFetcher<FileResponseInterface, SWRKey, string> = async (
    { url, ...config },
    { arg: hash },
) => {
    const hashUrl = url + hash + '/sandbox';
    const res = await axios.get(hashUrl, config);
    return res.data;
};

/** A community scan that resolved as a hash hit, parked in navigation state so the results page's
 *  Rescan button can replay it as an upload. */
export interface RescanState {
    file: File;
    rules?: string;
    sandbox?: boolean;
    privateScan?: boolean;
    archivePasswords?: string[];
    sendArchivePasswords?: boolean;
}

export const useFileScan = () => {
    const fileUploadKey: SWRKey = { url: '/v4/file' };
    const {
        trigger: upload,
        isMutating: uploadIsMutating,
        ...hookData
    } = useSWRMutation(fileUploadKey, uploadFile, { revalidate: false });
    const { trigger: lookup, isMutating: lookupIsMutating } = useSWRMutation(hashLookupKey, hashLookup);
    const { trigger: lookupSandbox } = useSWRMutation(hashLookupSandboxKey, hashLookupSandbox);
    const { data: limitsStatus, mutate: refetchLimitsStatus } = useApikeyLimits();
    const isLoading = lookupIsMutating || uploadIsMutating;
    const { toastNotify } = useToastNotify();
    const { user } = useUser();
    const { showLimitPromptForError } = useLimitPrompt();

    const { mutate: globalMutate } = useSWRConfig();

    // Shared submit + post-processing for both uploaded files and downloadfrom-URL scans.
    // `onError` lets a caller claim a failure it can recover from (return true) instead of showing the
    // default toast — e.g. a URL the backend cannot download, where the caller falls back to reputation
    // data. Aborts never reach it. Every other failure still toasts, so nothing is silently lost.
    const runUpload = useCallback(
        async (arg: Parameters<typeof upload>[0], tab?: string, onError?: (error: unknown) => boolean) => {
            try {
                const fileData = await upload(arg);

                if (fileData?.data_id) {
                    if (arg.file) {
                        lastScanSubmission = {
                            dataId: fileData.data_id,
                            file: arg.file,
                            rules: arg.rules,
                            sandbox: arg.sandbox,
                            privateScan: arg.privateScan,
                        };
                    }

                    const keys = new Set([
                        `/v4/hash/${fileData?.sha1}/sandbox`,
                        `/v4/hash/${fileData?.sha1}/vulnerability`,
                        `/v4/hash/${fileData?.sha1}/scanhistory`,
                        `/v4/comment/${fileData?.sha1}`,
                    ]);

                    await refetchLimitsStatus();

                    navigate(
                        `/results/file/${fileData.data_id}/${encodeURIComponent(tab || FileDetailsTabs.Overview)}`,
                        {
                            state: {
                                isSandboxOld: fileData?.sandbox_error ?? false,
                                dataId: fileData?.data_id,
                                limitsStatus,
                                sandboxId: fileData?.last_sandbox_id?.[0]?.sandbox_id || fileData?.sandbox_id,
                                /* The `rule` header exactly as sent. The results page reads it to know
                                   which technologies the scan asked for before any of them report
                                   back — the response cannot say that until the results land. */
                                rules: arg.rules,
                            },
                        },
                    );

                    globalMutate((key: AxiosConfig) => keys.has(key?.url ?? ''), undefined, { revalidate: true });
                }
            } catch (error) {
                const caughtError = error as {
                    code?: string;
                    name?: string;
                    message?: string;
                    messages?: string[];
                    response?: { data?: { error?: { message?: string; details?: { message?: string } } } };
                };
                if (caughtError?.code === 'ERR_CANCELED' || caughtError?.name === 'CanceledError') {
                    return;
                }
                /* Ahead of `onError` on purpose: a 429 is never a "backend could not download this URL"
                   case, so the limit prompt has to win over a caller's fallback (e.g. PublicScanCard
                   claiming the failure and navigating to reputation data), which would otherwise move
                   the user off the explanation of why the scan was refused. Returns true only once the
                   prompt is already open, so there is nothing left to show. */
                if (showLimitPromptForError(error)) {
                    return;
                }
                if (onError?.(error)) {
                    return;
                }
                let errorMessage = 'An error occurred while processing the file.';
                if (caughtError?.response?.data?.error?.details?.message) {
                    errorMessage = caughtError.response.data.error.details.message;
                } else if (caughtError?.response?.data?.error?.message) {
                    errorMessage = caughtError.response.data.error.message;
                } else if (caughtError?.messages?.length) {
                    errorMessage = caughtError.messages[0];
                } else if (caughtError?.message) {
                    errorMessage = caughtError.message;
                }
                toastNotify({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error',
                    hasClose: true,
                    duration: 5000,
                });
            }
        },
        [upload, refetchLimitsStatus, limitsStatus, globalMutate, toastNotify, showLimitPromptForError],
    );

    const handleUpload = useCallback(
        async (
            file: File,
            rules?: string,
            sandbox?: boolean,
            privateScan?: boolean,
            archivePasswords?: string[],
            onProgress?: (progress: number) => void,
            tab?: string,
            sendArchivePasswords?: boolean,
            signal?: AbortSignal,
        ) => {
            return runUpload(
                { file, rules, sandbox, privateScan, archivePasswords, sendArchivePasswords, onProgress, signal },
                tab,
            );
        },
        [runUpload],
    );

    // Scan a file the backend downloads from a URL (downloadfrom header) rather than an uploaded body.
    const handleScanUrl = useCallback(
        async (
            downloadUrl: string,
            rules?: string,
            sandbox?: boolean,
            privateScan?: boolean,
            tab?: string,
            signal?: AbortSignal,
            onError?: (error: unknown) => boolean,
        ) => {
            return runUpload({ downloadUrl, rules, sandbox, privateScan, signal }, tab, onError);
        },
        [runUpload],
    );

    /* Community tier scans hash-first (MDCI-1016): a file the platform already knows lands on its
       existing `/results/hash/` report — one reputation call, no Prevention unit — and the upload is
       deferred to the results page's Rescan button, which replays exactly these arguments. Paid users
       always upload: their scan options and workflows are the point of the scan. */
    const scansHashFirst = !isPaidUser(user);

    const handleScan = useCallback(
        async (
            file: File,
            rules?: string,
            sandbox?: boolean,
            privateScan?: boolean,
            archivePasswords?: string[],
            onProgress?: (progress: number) => void,
            tab?: string,
            sendArchivePasswords?: boolean,
            signal?: AbortSignal,
        ) => {
            if (scansHashFirst) {
                let known: FileResponseInterface | undefined;
                try {
                    known = await lookup(await getFileHash(file));
                } catch {
                    // Unhashable file or unknown hash (404): nothing to show yet, so scan it for real below.
                }
                const sha256 = known?.file_info?.sha256 ?? known?.sha256;
                // The card aborts the previous submission when another file is picked; a late hit must not navigate.
                if (sha256 && !signal?.aborted) {
                    // A known encrypted file still gets the password prompt: it re-submits these bytes
                    // against the `data_id` the hash page shows, exactly as after an upload.
                    if (known?.data_id) {
                        lastScanSubmission = { dataId: known.data_id, file, rules, sandbox, privateScan };
                    }
                    await refetchLimitsStatus();
                    navigate(`/results/hash/${sha256}/${encodeURIComponent(tab || FileDetailsTabs.Overview)}`, {
                        state: {
                            rescan: {
                                file,
                                rules,
                                sandbox,
                                privateScan,
                                archivePasswords,
                                sendArchivePasswords,
                            } satisfies RescanState,
                        },
                    });
                    return;
                }
            }
            return handleUpload(
                file,
                rules,
                sandbox,
                privateScan,
                archivePasswords,
                onProgress,
                tab,
                sendArchivePasswords,
                signal,
            );
        },
        [handleUpload, lookup, refetchLimitsStatus, scansHashFirst],
    );

    return {
        scan: handleScan,
        scanUrl: handleScanUrl,
        rescan: handleUpload,
        lookup,
        lookupSandbox,
        isLoading,
        limitsStatus,
        ...hookData,
    };
};
